import React, { Dispatch, PropsWithChildren, SetStateAction, useEffect, useMemo, useRef, useState, } from 'react';
import { Button, Drawer, Tooltip, Typography } from '@mui/material';
import { useDataGridEditPanelStyles } from './AdminDataGridStyles';
import { EditorModus } from '../EditDialog/EditDialog';
import { useTranslation } from 'react-i18next';
import * as MaterialUIIcons from '../../controls/MaterialUIIcons';
import IconButton from '@mui/material/IconButton';
import { TEntity } from '../../../../models/entity';
import { LagreEndringer } from '../LagreEndringer/lagreEndringer';
import { ErrorObject, useValidation } from '../../../utils/validateUtils';
import { useSelector } from 'react-redux';
import { selectOpen } from '../../../../features/layout/layoutSlice';
import { useEditPanelDragger } from './AdminDataGridHooks';
import { EditPanelProvider } from './EditPanelContext';
import { KeysArray } from '../../../../models/Constants/RequiredFields';
import { RegisterCodeName } from '../../../../models/Constants/ValgRegister';
import { FullscreenSpinner } from '../../controls/FullscreenSpinner/FullscreenSpinner';
import * as objectUtils from '../../../utils/objectUtils';
import { TMoveEntityEvt } from './AdminDataGrid';
import { ConfirmActionState, ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { unstable_useBlocker as useBlocker, useNavigate } from 'react-router-dom';
import { useBase } from '../../../utils/utilHooks';

export type DetailViewProps<T extends TEntity> = {
  mode: EditorModus;
  searchTerm?: T | string;
  id?: T['id'];
  value?: T;
  pristine: boolean;
  open?: boolean;
  setLoading: (val: boolean) => void;
};


type Props<T extends TEntity> = {
  getHeaderTextEditPanel?(id?: T['id'], mode?: EditorModus): string;
  itemTitle: string;
  registerId?: RegisterCodeName;
  mode: EditorModus;
  testId?: string;
  setValue?: Dispatch<SetStateAction<T | undefined>>;
  value?: T;
  initialValidateError?: ErrorObject;
  id?: T['id'];
  getDisplayName(id: T['id'], mode?: EditorModus): string | undefined;
  getErrors?: (
    fieldValues: Partial<T>,
    mode: EditorModus,
    fieldName?: string
  ) => ErrorObject;
  requiredFields: KeysArray<T>;
  searchTerm?: string;
  open: boolean;
  editPanelWidth: number;
  onClose(): void;
  saveChanges(val?: T, keepForm?: boolean): void;
  setEditPanelWidth(val: number): void;
  renderDetailView(props: DetailViewProps<T>): JSX.Element;
  loading?: boolean;
  moveEvt: TMoveEntityEvt;
  setMoveEvt: (evt: TMoveEntityEvt) => void;
  canCreate?: boolean;
  canCopy?: boolean;
  canEdit?: boolean;
};


export default function AdminDataGridEditPanel<T extends TEntity>({
  onClose,
  mode,
  id,
  value: initialValue,
  getErrors,
  saveChanges,
  requiredFields,
  editPanelWidth,
  setEditPanelWidth,
  setValue: setInitialValue,
  testId = '',
  initialValidateError,
  open,
  renderDetailView,
  moveEvt,
  setMoveEvt,
  canCopy = mode !== EditorModus.Vis,
  canEdit = mode !== EditorModus.Vis,
  canCreate = mode !== EditorModus.Vis,
  ...props
}: PropsWithChildren<Props<T>>) {
  const classes = useDataGridEditPanelStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirmActionState, setConfirmActionState] = useState<ConfirmActionState>();
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState<T | undefined>(undefined);
  const [isValid, setValid] = useState<boolean>(false);
  const [isPristine, setPristine] = useState<boolean>(true);
  const [headerButtons, setHeaderButtons] = useState<JSX.Element | null>();
  const [pathname, setPathname] = useState('');
  const canClose = useRef(false);
  const root = useBase() ?? '';

  useBlocker((args) => {
    if (canClose.current) {
      canClose.current = false;
      return false;
    }
    if (!isPristine && isValid && mode !== EditorModus.Soek) {
      setPathname(args.nextLocation.pathname.replace(root, '/'));
      setlagreEndringerOpen(true);
      return true;
    }
    return false;
  });


  const [lagreEndringerOpen, setlagreEndringerOpen] = useState<boolean>(false);
  const { width, isDragging, setIsDragging } = useEditPanelDragger(
    editPanelWidth,
    setEditPanelWidth
  );

  const { errors, validate, setErrors, validateField, handleFieldValidation } =
    useValidation(
      {
        requiredFields: requiredFields,
        mode: mode,
        setValid,
        initialValidateError,
        setPristine,
      },
      getErrors
    );
  useEffect(() => {
    setErrors({});
  }, [setErrors]);

  useEffect(() => {
    setConfirmActionState(undefined);
  }, [id, mode]);


  const detailView = useMemo(() => {
    return (
      <EditPanelProvider<T>
        setConfirmActionState={setConfirmActionState}
        setPristine={setPristine}
        setValid={setValid}
        setValue={setValue}
        setHeaderButtons={setHeaderButtons}
        setErrors={setErrors}
        errors={errors}
        handleFieldValidation={handleFieldValidation}
        mode={mode}
        validateField={validateField}
        validate={validate}
      >
        {' '}
        {renderDetailView({
          id: id,
          setLoading,
          value: initialValue,
          mode: mode,
          open: open,
          pristine: isPristine,
        })}
      </EditPanelProvider>
    );
  }, [
    renderDetailView,
    id,
    setErrors,
    handleFieldValidation,
    isPristine,
    initialValue,
    mode,
    errors,
    validate,
    setHeaderButtons,
    open,
    setLoading,
    validateField,
    setValue,
    setPristine,
  ]);

  const openToolbar = useSelector(selectOpen);

  // function getHeaderText() {
  //   if (props.getHeaderTextEditPanel)
  //     return props.getHeaderTextEditPanel(id, mode);

  //   switch (mode) {
  //     case EditorModus.Kopi:
  //       return `Kopi av ${props.itemTitle}`;
  //     case EditorModus.OpprettBarn:
  //     case EditorModus.Opprett:
  //       return `${t('Opprett ')} ${props.itemTitle}`;
  //     case EditorModus.Endre:
  //       return `${t('Redigere ')}${id ? props.getDisplayName(id, mode) ?? '' : ''
  //         }`;
  //     case EditorModus.Vis:
  //       return `${t('Viser ')}${id ? props.getDisplayName(id, mode) ?? '' : ''
  //         }`;
  //     case EditorModus.Soek:
  //       return t('Søk');
  //     default:
  //       return '';
  //   }
  // }
  function getHeaderText() {
    if (props.getHeaderTextEditPanel) {
      return (
        <span test-id={testId + '-header'}>
          {props.getHeaderTextEditPanel(id, mode)}
        </span>
      );
    }
  
    let headerText;
    switch (mode) {
      case EditorModus.Kopi:
        headerText = `Kopi av ${props.itemTitle}`;
        break;
      case EditorModus.OpprettBarn:
      case EditorModus.Opprett:
        headerText = `${t('Opprett ')} ${props.itemTitle}`;
        break;
      case EditorModus.Endre:
        headerText = `${t('Redigere ')}${id ? props.getDisplayName(id, mode) ?? '' : ''}`;
        break;
      case EditorModus.Vis:
        headerText = `${t('Viser ')}${id ? props.getDisplayName(id, mode) ?? '' : ''}`;
        break;
      case EditorModus.Soek:
        headerText = t('Søk');
        break;
      default:
        headerText = '';
    }
  
    return (
      <span test-id={testId + '-header'}>
        {headerText}
      </span>
    );
  }
  const headerText = getHeaderText();

  function checkIsFormDisabled() {
    if ([EditorModus.Opprett, EditorModus.OpprettBarn].includes(mode) && !canCreate) return true;
    if (mode === EditorModus.Kopi && !canCopy) return true;
    if (mode === EditorModus.Endre && !canEdit) return true;
    if (mode === EditorModus.Vis) return true;
  }

  const formDisabled = checkIsFormDisabled();

  function closeDetail() {
    if (!isPristine && isValid && mode !== EditorModus.Soek) {
      if (value) validate(value);
    }
    onClose();
  }

  function ignorere() {
    setPristine(true);
    canClose.current = true;
    setlagreEndringerOpen(false);
    navigate(pathname);
  }

  function avbryt() {
    setlagreEndringerOpen(false);
    canClose.current = true;
    if (moveEvt === 'clickEntity') {
      navigate(pathname);
    }
  }

  function handleLagreConfirm() {
    if (confirmActionState && confirmActionState.mode === mode) {
      handleOpenConfirmAction(lagre);
    } else lagre();
  }

  function lagre() {
    canClose.current = true;
    saveChanges(value, false);
    setlagreEndringerOpen(false);
    navigate(pathname);
  }

  function closeConfirm() {
    setConfirmActionState(prev => prev ? ({ ...prev, open: false }) : undefined);
  }

  function handleOpenConfirmAction(action: () => void) {
    setConfirmActionState(prev => ({
      ...prev!,
      open: true,
      onConfirm: async () => {
        setLoading(true);
        await prev!.onConfirm?.();
        setLoading(false);
        action();
      },
      onClose: () => {
        prev!.onClose?.();
        closeConfirm();
      }
    }));
  }

  async function handleSaveSubmit() {
    if (confirmActionState && confirmActionState.mode === mode) {
      handleOpenConfirmAction(save);
    } else await save();
  }

  async function save() {
    const valid = validate(value as T);
    if (valid || mode === EditorModus.Soek) {
      setPristine(true);
      await saveChanges(value, true);
    }
  }

  function handleKeys(e: any) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if ((mode === EditorModus.Soek && e.key === 'Enter')) {
      e.preventDefault();
      saveChanges(value);
      return;
    }
    if (mode !== EditorModus.Soek && e.ctrlKey && e.key === 'Enter') {
      save();
      return;
    }
  }

  let submitButton;
  switch (mode) {
    case EditorModus.Vis:
      submitButton = undefined;
      break;
    case EditorModus.Soek: {
      const isEmpty = objectUtils.isEmpty(value);
      submitButton = (
        <>
          <Tooltip title={mode === EditorModus.Soek ? 'Enter' : 'Ctrl+Enter'}>
            <Button
              color='secondary'
              variant='contained'
              test-id={testId + '-sok-btn'}
              className={classes.saveButton}
              onClick={save}
            >
              {' '}
              <Typography
                variant='subtitle1'
                component='h2'
                className={classes.saveButton}
              >
                {'Søk'}
              </Typography>
            </Button>
          </Tooltip>
          <Button
            color='secondary'
            variant='outlined'
            disabled={isEmpty}
            className={classes.saveButton}
            test-id={testId + '-tøm-btn'}
            onClick={() => {
              if (setInitialValue) setInitialValue({} as T);
            }}
          >
            <Typography
              variant='subtitle1'
              component='h2'
              color={isEmpty ? 'white' : undefined}
              className={classes.saveButton}
            >
              {'Tøm'}
            </Typography>
          </Button>
        </>
      );
      break;
    }
    default:
      submitButton = (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {headerButtons}
          <Button
            color='secondary'
            variant={isPristine || !isValid ? 'outlined' : 'contained'}
            disabled={isPristine || !isValid || formDisabled}
            test-id={testId + '-lagre-btn'}
            className={classes.saveButton}
            onClick={handleSaveSubmit}
          >
            {' '}
            <Typography
              variant='subtitle1'
              component='h2'
              className={classes.saveButton}
            >
              {t('Lagre')}
            </Typography>
          </Button>
        </div>
      );
      break;
  }

  return (
    <>
      <div
        onKeyUp={(e) => {
          handleKeys(e);
        }}
        tabIndex={-1}
      >
        <LagreEndringer
          title={'Lagre endringer'}
          children={
            <Typography>
              Du har gjort endringer som ikke er lagret. Ønsker du å lagre
              endringene?
            </Typography>
          }
          open={lagreEndringerOpen}
          isValid={isValid}
          ignorere={ignorere}
          avbryt={avbryt}
          onClose={() => {
          }}
          lagre={handleLagreConfirm}
        />
        <Drawer
          variant='permanent'
          anchor='right'
          onClose={() => onClose()}
          PaperProps={{
            className: classes.drawer,
            style: {
              maxWidth: openToolbar
                ? 'calc(100vw - 280px)'
                : 'calc(100vw - 80px)',
              width: open ? width : 0,
              minWidth: open ? undefined : 0,
              transition: isDragging ? 'none' : undefined,
            },
          }}
        >
          <div
            className={classes.resizeHandle}
            onMouseDown={(evt) => {
              evt.preventDefault();
              setIsDragging(true);
            }}
          />
          <form
            className={classes.form}
            id={'edit-panel-form'}
            onSubmit={(evt) => {
              const valid = validate(value as T);
              if (valid) {
                evt.preventDefault();
                saveChanges(value);
                setEditPanelWidth(width);
                setPristine(true);
              }
            }}
          >
            <div className={classes.mainHeader}>
              <Typography
                variant='subtitle1'
                component='h2'
                className={classes.headerText}
              >
                {headerText}
              </Typography>

              {submitButton}
              <IconButton
                color='secondary'
                className={classes.iconClose}
                aria-label={t('Lukk')}
                onClick={() => closeDetail()}
                test-id={testId + '-lukk-btn'}
              >
                <MaterialUIIcons.CloseIcon color='secondary' />
              </IconButton>
            </div>
            <FullscreenSpinner loading={props.loading || loading} />
            <fieldset
              disabled={formDisabled}
              className={classes.content}
            >
              {detailView}
            </fieldset>
            {
              confirmActionState &&
              <ConfirmDialog
                title={confirmActionState.title}
                confirmBtnTitle={confirmActionState.confirmBtnTitle}
                abortBtnTitle={confirmActionState.abortBtnTitle}
                open={confirmActionState.open}
                onClose={confirmActionState.onClose}
                hideOnCloseIcon={true}
                onConfirm={confirmActionState.onConfirm}
                children={<span style={{
                  whiteSpace: 'pre-wrap',
                  display: 'block'
                }}>{t(confirmActionState.message)}</span>}
              />
            }
          </form>
        </Drawer>
      </div>
    </>
  );
}
